import { normalizeForSearch } from "app/pages/sales-intelligence/services/fuzzy-search/normalize";
import { revealAncestors } from "app/pages/sales-intelligence/utils/taxonomy-tree/tree";
import type { TaxonomyIndex } from "app/pages/sales-intelligence/utils/taxonomy-tree/types";

/**
 * Matching the whole taxonomy would reveal every ancestor chain of every hit, which mounts most of the
 * tree for a one-letter query. Industry caps at the same number for the same reason.
 */
const MAX_RESULTS = 50;

export type TaxonomySearch<TId extends string> = {
    /** Ids to render for a query, ancestors included. `undefined` means "no search, show everything". */
    visibleNodeIds(searchQuery: string): ReadonlySet<TId> | undefined;
};

/**
 * Owns substring search for one taxonomy, independent of the current selection.
 *
 * Substring rather than fuzzy is deliberate: these panels want an exact, cheap filter and paint no
 * match highlights, so the fuse-backed `createIndustrySearchIndex` — which exists to tolerate typos
 * and return ranges — would buy nothing here. The two are not duplicates to be unified.
 *
 * Selection and search are separate inputs, but the view model is rebuilt on every checkbox click — so
 * a search living inside it re-scans every node on each click while a query is active. Holding it here
 * keeps `visibleNodeIds` referentially stable across selections, and lets the normalised names and the
 * per-query result be computed once rather than per keystroke.
 */
export const createSubstringTaxonomySearch = <TId extends string>(
    taxonomy: TaxonomyIndex<TId>,
    locale: string,
): TaxonomySearch<TId> => {
    /**
     * Built on the first query rather than up front: normalising every name costs a pass over the whole
     * taxonomy — ~20k entries for intent — and most sessions open the panel without ever typing.
     */
    let normalizedNames: Map<TId, string> | null = null;

    const namesByNode = (): Map<TId, string> => {
        if (!normalizedNames) {
            const names = new Map<TId, string>();

            taxonomy.byId.forEach((node, id) => {
                names.set(id, normalizeForSearch(node.name, locale));
            });

            normalizedNames = names;
        }

        return normalizedNames;
    };

    let cachedQuery: string | null = null;
    let cachedVisible: ReadonlySet<TId> | undefined;

    /**
     * Only the node's own name is matched, never its ancestor path: indexing the path would make a bare
     * parent term match every descendant, and revealing that match set mounts the entire branch.
     */
    const matchNames = (normalizedQuery: string): Set<TId> => {
        const matched = new Set<TId>();

        for (const [id, name] of namesByNode()) {
            if (name.includes(normalizedQuery)) {
                matched.add(id);

                if (matched.size === MAX_RESULTS) {
                    break;
                }
            }
        }

        return matched;
    };

    return {
        visibleNodeIds(searchQuery) {
            const normalizedQuery = normalizeForSearch(searchQuery, locale);

            if (!normalizedQuery) {
                return undefined;
            }

            if (cachedQuery !== normalizedQuery) {
                cachedQuery = normalizedQuery;
                cachedVisible = revealAncestors(matchNames(normalizedQuery), taxonomy);
            }

            return cachedVisible;
        },
    };
};
